import React, { useEffect, useState, useCallback } from "react";
import {
  collection,
  doc,
  getDocs,
  deleteDoc,
  addDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import BookmarkExportView from "./BookmarkExportView";
import BookmarkImportView from "./BookmarkImportView";
import { colors, fontSizes, spacing, buttons, rounded } from "../styles/tokens";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";

export default function FloatingFilePanel({
  user,
  onClose,
  reloadCallback,
  sharedImportData = [],
}) {
  const [videos, setVideos] = useState({});
  const [expandedVideos, setExpandedVideos] = useState({});
  const [selected, setSelected] = useState({});
  const [mode, setMode] = useState("default");
  const [loading, setLoading] = useState(false);

  const loadAllVideos = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const videoSnap = await getDocs(collection(db, "users", user.uid, "videos"));
    const out = {};
    for (const videoDoc of videoSnap.docs) {
      const videoId = videoDoc.id;
      const videoData = videoDoc.data();
      const sectionSnap = await getDocs(
        collection(db, "users", user.uid, "videos", videoId, "bookmarks")
      );
      const sections = sectionSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      out[videoId] = {
        title: videoData.title || "(Untitled)",
        sections,
      };
    }
    setVideos(out);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (user && mode === "default") {
      loadAllVideos();
    }
  }, [user, mode, loadAllVideos]);

  if (sharedImportData?.length > 0) {
    return (
      <BookmarkImportView
        user={user}
        preloadedData={sharedImportData}
        onBack={() => (window.location.href = "/")}
        reloadCallback={reloadCallback}
      />
    );
  }

  const toggleVideoExpanded = (videoId) => {
    setExpandedVideos((prev) => ({
      ...prev,
      [videoId]: !prev[videoId],
    }));
  };

  const toggleSelect = (videoId, sectionId = null) => {
    const key = sectionId ? `${videoId}__${sectionId}` : videoId;

    setSelected((prev) => {
      const copy = { ...prev };
      if (copy[key]) {
        delete copy[key];
      } else {
        copy[key] = true;
      }

      // Toggling a whole video also toggles its sections
      if (!sectionId) {
        const bookmarks = videos[videoId]?.sections || [];
        bookmarks.forEach((b) => {
          const k = `${videoId}__${b.id}`;
          if (copy[videoId]) {
            copy[k] = true;
          } else {
            delete copy[k];
          }
        });
        return copy;
      }
      return copy;
    });
  };

  const isSelected = (videoId, sectionId = null) => {
    const key = sectionId ? `${videoId}__${sectionId}` : videoId;
    return !!selected[key];
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete selected videos and/or bookmarks?"
    );
    if (!confirmed) return;

    const affectedVideoIds = new Set();

    try {
      for (const [videoId, videoData] of Object.entries(videos)) {
        const isVideoSelected = isSelected(videoId);

        if (isVideoSelected) {
          const sectionSnap = await getDocs(
            collection(db, "users", user.uid, "videos", videoId, "bookmarks")
          );
          const deleteBookmarkPromises = sectionSnap.docs.map((docSnap) =>
            deleteDoc(docSnap.ref)
          );
          await Promise.all(deleteBookmarkPromises);
          await deleteDoc(doc(db, "users", user.uid, "videos", videoId));
          affectedVideoIds.add(videoId);
        } else {
          for (const section of videoData.sections) {
            if (isSelected(videoId, section.id)) {
              const sectionRef = doc(
                db,
                "users",
                user.uid,
                "videos",
                videoId,
                "bookmarks",
                section.id
              );
              await deleteDoc(sectionRef);
              affectedVideoIds.add(videoId);
            }
          }
        }
      }

      alert("Selected items deleted.");
      setSelected({});
      setExpandedVideos({});
      await loadAllVideos();

      if (reloadCallback && typeof reloadCallback === "function") {
        for (const videoId of affectedVideoIds) {
          await reloadCallback(videoId);
        }
      }
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete selected items.");
    }
  };

  const handleExport = async () => {
    const selectedBookmarks = [];

    for (const [videoId, videoData] of Object.entries(videos)) {
      for (const section of videoData.sections) {
        const key = `${videoId}__${section.id}`;
        if (selected[key]) {
          selectedBookmarks.push(section);
        }
      }
    }

    if (selectedBookmarks.length === 0) {
      alert("Please select at least one section to export.");
      return;
    }

    try {
      const sharedRef = await addDoc(collection(db, "sharedExports"), {
        createdAt: new Date(),
        data: selectedBookmarks,
      });
      const shareLink = `${window.location.origin}/shared/${sharedRef.id}`;
      await navigator.clipboard.writeText(shareLink);
      alert(`Shareable link copied to clipboard:\n${shareLink}`);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Failed to create shareable export.");
    }
  };

  const handleSelectAllToggle = () => {
    const newState = {};
    const allChecked = Object.entries(videos).every(([vid]) => isSelected(vid));
    if (!allChecked) {
      for (const [videoId, videoData] of Object.entries(videos)) {
        newState[videoId] = true;
        videoData.sections.forEach((s) => {
          newState[`${videoId}__${s.id}`] = true;
        });
      }
    }
    setSelected(allChecked ? {} : newState);
  };

  const allChecked =
    Object.entries(videos).length > 0 &&
    Object.entries(videos).every(([vid]) => isSelected(vid));

  if (mode === "export") {
    return (
      <BookmarkExportView
        user={user}
        onBack={() => setMode("default")}
        reloadCallback={reloadCallback}
      />
    );
  }

  if (mode === "import") {
    return (
      <BookmarkImportView
        user={user}
        onBack={() => setMode("default")}
        reloadCallback={reloadCallback}
      />
    );
  }

  return (
    <div
      className={`fixed top-4 right-4 w-[95vw] max-w-sm z-50 max-h-[90vh] overflow-y-auto
        ${colors.surface} border shadow-lg ${spacing.md} ${rounded.md} ${fontSizes.sm}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className={`flex items-center ${spacing.gapSm}`}>
          <input
            type="checkbox"
            checked={allChecked}
            onChange={handleSelectAllToggle}
            className="h-4 w-4"
            aria-label="Select all"
          />
          <div className="flex items-center gap-2 font-semibold">
            <h2 className="leading-none">📂 Bookmark Manager</h2>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-black text-lg leading-none px-2"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* Body */}
      {loading ? (
        <p className={`${fontSizes.sm} text-gray-600`}>Loading...</p>
      ) : (
        <div className="space-y-3">
          {Object.entries(videos).map(([videoId, video]) => {
            const expanded = !!expandedVideos[videoId];
            return (
              <div key={videoId} className={`border ${rounded.md}`}>
                <div
                  className="flex justify-between items-center cursor-pointer bg-gray-100 hover:bg-gray-200 px-3 py-2"
                  onClick={() => toggleVideoExpanded(videoId)}
                >
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={isSelected(videoId)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleSelect(videoId);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Select video"
                    />
                    <span className="font-medium leading-snug">{video.title}</span>
                  </label>
                  {expanded ? (
                    <IconChevronUp size={16} className="text-gray-500 shrink-0" />
                  ) : (
                    <IconChevronDown size={16} className="text-gray-500 shrink-0" />
                  )}
                </div>

                {expanded && (
                  <div className="bg-white px-4 py-2 space-y-1">
                    {video.sections.map((s) => (
                      <label
                        key={s.id}
                        className={`flex items-center gap-3 ${fontSizes.sm}`}
                        title={`${s.title} ${s.start.toFixed(1)}–${s.end.toFixed(1)} @ ${s.speed.toFixed(2)}x`}
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={isSelected(videoId, s.id)}
                          onChange={() => toggleSelect(videoId, s.id)}
                          aria-label={`Select ${s.title}`}
                        />
                        <span className="leading-snug">
                          {s.title} : {s.start.toFixed(1)} - {s.end.toFixed(1)}, {s.speed.toFixed(2)}x
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer actions */}
      <div className={`flex ${spacing.gapMd} mt-6 border-t pt-4`}>
        <button
          onClick={handleExport}
          className={`${buttons.whitePurple} ${buttons.purpleHover} flex-1`}
        >
          Export
        </button>
        <button
          onClick={handleDelete}
          className="bg-red-600 text-white px-3 py-2 rounded text-sm flex-1"
        >
          Delete
        </button>
      </div>
    </div>
  );
}
